// src/stores/useSettings.ts
import { create } from 'zustand'
import { supabase } from '../services/supabase'
import type { Settings } from '../types'
import { DEFAULT_THEME, THEMES } from '../constants/themes'

const DEFAULT_SETTINGS: Settings = {
  theme: DEFAULT_THEME,
  signal_slots: 5,
  noise_budget_mins: 45,
  workday_hrs: 8,
  ai_classification: true,
  ai_coaching: true,
  smart_nudges: true,
  ai_provider: 'groq',
  user_api_key: '',
  ai_context: '',
  daily_reminder_enabled: false,
  daily_reminder_time: '09:00',
  cleanup_regularity: 'daily',
  tour_done: false,
  is_premium: false,
  snr_notification_threshold: 75,
  snr_notification_frequency: 'once_hourly',
  coaching_tone: 'mentor',
  onboarding_done: false,
}

interface SettingsState {
  settings: Settings
  loading: boolean
  load: () => Promise<void>
  update: (patch: Partial<Settings>) => Promise<void>
}

export const useSettings = create<SettingsState>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  loading: false,

  load: async () => {
    set({ loading: true })
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      const { data } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()
      if (data) {
        set({ settings: { ...DEFAULT_SETTINGS, ...data } })
        applyThemeToDOM(data.theme)
      } else {
        applyThemeToDOM(DEFAULT_THEME)
      }
    } catch { /* non-fatal */ } finally {
      set({ loading: false })
    }
  },

  update: async (patch: Partial<Settings>) => {
    const next = { ...get().settings, ...patch }
    set({ settings: next })
    // Apply theme to DOM immediately
    if (patch.theme) applyThemeToDOM(patch.theme)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      await supabase.from('user_settings').upsert({ user_id: user.id, ...next }, { onConflict: 'user_id' })
    } catch { /* non-fatal */ }
  },
}))

export function applyThemeToDOM(themeName: string) {
  const t = THEMES[themeName as keyof typeof THEMES]
  if (!t) return
  const root = document.documentElement
  const vars: Record<string, string> = {
    '--bg': t.bg,
    '--card-low': t.cardLow,
    '--card-mid': t.cardMid,
    '--card-high': t.cardHigh,
    '--border': t.border,
    '--text': t.text,
    '--text-muted': t.textMuted,
    '--text-dim': t.textDim,
    '--accent': t.accent,
    '--accent-dim': t.accentDim,
    '--signal': t.signal,
    '--noise': t.noise,
    '--amber': t.amber,
  }
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v)
}
